import { useMemo, useState, useEffect } from 'react';
import { useComments } from '@/hooks/useComments';
import { useAuth } from '@/contexts/AuthContext';
import CommentForm from './CommentForm';
import CommentThread from './CommentThread';

interface CommentSectionProps {
  threadId: string;
}

export default function CommentSection({ threadId }: CommentSectionProps) {
  const { comments, isLoading, isConnected, postComment, editComment, deleteComment, toggleUpvote } =
    useComments(threadId);
  const { user, isSuperadmin } = useAuth();

  // Single interval that re-renders this component (and all CommentThread children)
  // every minute so timeAgo labels stay current without per-comment timers.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const totalCount = useMemo(() => {
    function count(acc: number, node: (typeof comments)[number]): number {
      return acc + 1 + node.childNodes.reduce(count, 0);
    }
    return comments.reduce(count, 0);
  }, [comments]);

  return (
    <div className="mt-10 border-t border-gray-200 pt-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {totalCount > 0 ? `${totalCount} Comment${totalCount !== 1 ? 's' : ''}` : 'Comments'}
        </h2>
        {user && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-300'}`} />
            {isConnected ? 'Live' : 'Connecting…'}
          </div>
        )}
      </div>

      {/* Top-level compose form */}
      <div className="mb-8">
        {user ? (
          <CommentForm
            onSubmit={(body) => postComment(body)}
            placeholder="Share your thoughts…"
            submitLabel="Comment"
          />
        ) : (
          <p className="text-sm text-gray-400">
            <a href="/login" className="text-blue-500 hover:underline">
              Log in
            </a>{' '}
            to leave a comment.
          </p>
        )}
      </div>

      {/* Comment list */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-400">No comments yet. Be the first!</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {comments.map((node) => (
            <CommentThread
              key={node.id}
              node={node}
              currentUserId={user?.id}
              isSuperadmin={isSuperadmin}
              onReply={(body, parentId) => postComment(body, parentId)}
              onEdit={editComment}
              onDelete={deleteComment}
              onUpvote={toggleUpvote}
            />
          ))}
        </div>
      )}
    </div>
  );
}
